import React, { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import avatarSrc from "../../../images/memoji.png";
import { GreetingBubbleProps } from "./types";
import { getLocation } from "../../../util/location";

// Define region mappings and greetings
export const REGION_GREETINGS = {
  "United States": {
    countries: ["US", "USA", "United States", "United States of America"],
    headline: "Howdy from Propelius 🇮🇳 → 🇺🇸",
    body: "We're building lightning-fast products for NYC fintechs and Bay-Area start-ups—while overlapping 4+ hrs with EST. Want to chat about a 90-Day MVP, AI automation or staff augmentation? Hit me with your questions, and I'll lasso the answers faster than you can say 'y'all'!",
  },
  "Australia & NZ": {
    countries: ["AU", "AUS", "Australia", "NZ", "New Zealand"],
    headline: "G'day from Propelius (coding while you snooze)",
    body: "Our dev squad ships code overnight so you wake up to fresh features. Trusted by teams from Sydney to Perth, invoicing in AUD, GST-compliant. Curious about risk-sharing fixed-price sprints or a React talent boost? Give me a bell—no worries!",
  },
  "United Kingdom": {
    countries: [
      "GB",
      "UK",
      "United Kingdom",
      "Great Britain",
      "England",
      "Scotland",
      "Wales",
      "Northern Ireland",
    ],
    headline: "Cheers from Propelius—GMT-friendly, GDPR-ready",
    body: "Serving London scale-ups and Manchester SaaS crews with ISO 27001-aligned practices. Invoices in sterling, stand-ups in your timezone, plenty of tea on our end. Ask me anything about our 90-Day MVP offer or adding senior devs to your squad—mind the gap between idea and launch!",
  },
  "Continental Europe": {
    countries: [
      "DE",
      "FR",
      "IT",
      "ES",
      "PT",
      "NL",
      "BE",
      "LU",
      "AT",
      "CH",
      "SE",
      "NO",
      "DK",
      "FI",
      "Germany",
      "France",
      "Italy",
      "Spain",
      "Portugal",
      "Netherlands",
      "Belgium",
      "Luxembourg",
      "Austria",
      "Switzerland",
      "Sweden",
      "Norway",
      "Denmark",
      "Finland",
      "Poland",
      "Czech Republic",
      "Hungary",
      "Romania",
      "Bulgaria",
      "Greece",
      "Croatia",
      "Slovenia",
      "Slovakia",
      "Estonia",
      "Latvia",
      "Lithuania",
    ],
    headline: "Hallo/Hola/Salut—Propelius at your service",
    body: "From Berlin to Barcelona, EU innovators trust us for compliant, rock-solid software. We overlap CET hours, sign EU-law friendly contracts, and bill in EUR. Need a risk-shared MVP or a dedicated squad? Type away—Poppy answers in seconds, not Schengen time.",
  },
};

const GreetingBubble: React.FC<GreetingBubbleProps> = ({
  onClose,
  onChatOpen,
  countdown,
  isMobile,
  countryName,
  countryCode,
}) => {
  const defaultGreeting = {
    headline: "Hello, I'm Poppy!",
    body: "I'm Poppy, your virtual assistant, here to answer any questions you have about our services, projects, or how we can help your business. What would you like to know today?",
  };

  const [greeting, setGreeting] = useState(defaultGreeting);

  useEffect(() => {
    if (countryName || countryCode) {
      const matchingRegion = Object.entries(REGION_GREETINGS).find(
        ([_, regionData]) =>
          regionData.countries.some((c) => {
            const countryLower = c.toLowerCase();
            return (
              (countryName && countryName.toLowerCase() === countryLower) ||
              (countryCode && countryCode.toLowerCase() === countryLower)
            );
          })
      );

      if (matchingRegion) {
        setGreeting({
          headline: matchingRegion[1].headline,
          body: matchingRegion[1].body,
        });
      }
    }
  }, [countryName, countryCode]);

  return (
    <div
      onClick={onChatOpen}
      className="relative mb-4 w-11/12 cursor-pointer space-y-2 rounded-lg border border-gray-300 bg-white p-4 text-gray-800 shadow-lg after:absolute after:bottom-[-9px] after:right-[20px] after:h-[16px] after:w-[16px] after:rotate-45 after:border-r after:border-b after:border-gray-300 after:bg-white after:shadow-lg sm:max-w-md"
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close greeting"
        className="absolute top-2 right-2 p-1 text-gray-500 hover:text-gray-700"
      >
        <FaTimes className="text-lg" />
      </button>
      <div className="flex items-center space-x-3">
        <img
          src={avatarSrc}
          alt="Poppy Avatar"
          className="h-12 w-12 rounded-full"
        />
        <h3 className="text-lg font-semibold">{greeting.headline}</h3>
      </div>
      <p className="text-sm">{greeting.body}</p>
      {isMobile && (
        <p className="text-right text-xs text-gray-500">
          Closing in {countdown}s
        </p>
      )}
    </div>
  );
};

export default GreetingBubble;
